import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import PhoneMockup from "@/components/landing/PhoneMockup";
import VideoDemoModal from "@/components/landing/VideoDemoModal";
import MobileStickyCTA from "@/components/landing/MobileStickyCTA";
import TrustedBy from "@/components/landing/TrustedBy";
import BookingFlow from "@/components/landing/BookingFlow";
import Dashboard from "@/components/landing/Dashboard";
import Testimonials from "@/components/landing/Testimonials";
import Faq from "@/components/landing/Faq";
import FinalCta from "@/components/landing/FinalCta";
import LandingFooter from "@/components/landing/Footer";
import { Reveal, RevealStagger, RevealItem } from "@/components/landing/Reveal";
import { toast } from "sonner";
import axios from "axios";

import {
  Calendar,
  Users,
  Clock,
  BarChart3,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Menu,
  X,
  Check,
  Crown,
  PlayCircle,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";

const LandingPage = () => {
  const { isAuthenticated, user, api } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [salonName, setSalonName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [currentSystem, setCurrentSystem] = useState(
    "Taking bookings via messages",
  );
  const [loading, setLoading] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate(user?.role === "staff" ? "/staff" : "/dashboard");
    } else {
      navigate("/auth/register");
    }
  };

  const features = [
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description:
        "Let customers book appointments 24/7 with real-time availability updates.",
    },
    {
      icon: Users,
      title: "Staff Management",
      description:
        "Manage your team's schedules, assign services, and track performance.",
    },
    {
      icon: Clock,
      title: "Fewer No-shows",
      description:
        "Automated SMS & email reminders plus deposit collection at booking.",
    },
    {
      icon: BarChart3,
      title: "Business Insights",
      description:
        "Track bookings, revenue potential, and customer trends at a glance.",
    },
  ];

  const outcomes = [
    { kpi: "74%", label: "Clients Prefer Online Booking", icon: TrendingUp },
    { kpi: "−38%", label: "Reduction in No-shows", icon: Clock },
    { kpi: "8hrs", label: "Admin Time Saved Weekly", icon: Sparkles },
  ];

  const benefits = [
    "No more double bookings",
    "Clients book 24/7 — even while you sleep",
    "Deposits & secure online payments",
    "Branded booking page in minutes",
    "Works perfectly on mobile",
    "Australian-based support",
  ];

  const [apiPlans, setApiPlans] = useState([]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API_URL}/api/subscription/plans`);
        setApiPlans(response.data.plans || []);
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      }
    };
    fetchPlans();
  }, []);

  const scopeClass = "landing-violet";

  return (
    <div className={`${scopeClass} min-h-screen pb-20 md:pb-0`}>
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="container-marketing max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-2xl iridescent-text">
                SlotBook
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-7">
              <a
                href="#features"
                className="text-sm font-medium t-fg-muted t-hover-fg transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-sm font-medium t-fg-muted t-hover-fg transition-colors"
              >
                How it Works
              </a>
              <a
                href="#pricing"
                className="text-sm font-medium t-fg-muted t-hover-fg transition-colors"
              >
                Pricing
              </a>
              <a
                href="#faq"
                className="text-sm font-medium t-fg-muted t-hover-fg transition-colors"
              >
                FAQ
              </a>
              <Link
                to="/auth/login"
                className="text-sm font-medium t-fg t-hover-fg transition-colors"
              >
                Sign In
              </Link>
              <Button
                onClick={() => navigate("/auth/register")}
                className="t-bg-cta noir-cta-glow font-semibold"
                data-testid="get-started-btn"
              >
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <button
                className="p-2.5 rounded-xl t-fg transition-all active:scale-95"
                style={{ background: "transparent" }}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-nav-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              className="md:hidden py-4 border-t animate-fade-in"
              style={{ borderColor: "var(--t-surface-border)" }}
            >
              <nav className="flex flex-col gap-3">
                <a
                  href="#features"
                  className="text-sm font-medium t-fg-muted py-2"
                >
                  Features
                </a>
                <a
                  href="#how-it-works"
                  className="text-sm font-medium t-fg-muted py-2"
                >
                  How it Works
                </a>
                <a
                  href="#pricing"
                  className="text-sm font-medium t-fg-muted py-2"
                >
                  Pricing
                </a>
                <a href="#faq" className="text-sm font-medium t-fg-muted py-2">
                  FAQ
                </a>
                <Link
                  to="/auth/login"
                  className="text-sm font-medium t-fg-muted py-2"
                >
                  Sign In
                </Link>
                <div
                  className="pt-4 mt-2 space-y-3 border-t"
                  style={{ borderColor: "var(--t-surface-border)" }}
                >
                  <Button
                    onClick={() => navigate("/auth/register")}
                    className="w-full h-11 t-bg-cta font-semibold"
                    data-testid="get-started-btn-mobile"
                  >
                    Start Free Trial
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    // onClick={() => setVideoOpen(true)}
                    onClick={() => {
                      setDemoOpen(true);
                      setDemoSuccess(false);
                    }}
                    variant="outline"
                    className="w-full h-11 t-bg-secondary"
                  >
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Book Demo
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden pt-12 pb-16 sm:pt-16 sm:pb-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 iridescent-mesh-bg"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 noir-vignette"
        />

        <div className="container-marketing max-w-7xl mx-auto relative px-4 sm:px-6">
          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-12 lg:gap-10 items-center">
            <motion.div
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: {
                  transition: { staggerChildren: 0.18, delayChildren: 0.1 },
                },
              }}
            >
              <motion.h1
                variants={{
                  hidden: { opacity: 0, y: 48 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-[-0.05em] leading-[0.95] mb-6 t-fg"
              >
                Get More Bookings,
                <br />
                <span className="iridescent-text">Manage less.</span>
              </motion.h1>

              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 32 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="text-base sm:text-lg t-fg-muted mb-8 max-w-xl leading-relaxed"
              >
                Online booking and salon management software for Australian
                salons, beauty studios and makeup artists. Fill your calendar,
                reduce no-shows and spend less time on admin.
              </motion.p>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <motion.div
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 360, damping: 22 }}
                >
                  <Button
                    size="lg"
                    onClick={() => navigate("/auth/register")}
                    className="w-full sm:w-auto text-base t-bg-cta noir-cta-glow h-14 px-7 font-semibold rounded-full"
                    data-testid="hero-cta-trial"
                  >
                    Start Free Trial
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 360, damping: 22 }}
                >
                  <Button
                    size="lg"
                    // onClick={() => setVideoOpen(true)}
                    onClick={() => {
                      setDemoOpen(true);
                      setDemoSuccess(false);
                    }}
                    variant="outline"
                    className="w-full sm:w-auto text-base t-bg-secondary h-14 px-7 font-semibold rounded-full backdrop-blur"
                    data-testid="hero-cta-watch-demo"
                  >
                    <PlayCircle className="w-5 h-5 mr-2" />
                    Book Demo
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
                  },
                }}
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs t-fg-muted"
              >
                {[
                  "30-days free trial",
                  "No credit card",
                  "Cancel anytime",
                  "AU-based support",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 t-success" />
                    <span>{t}</span>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right: Phone mockup only */}
            <div className="relative flex items-center justify-center lg:justify-end mt-6 lg:mt-0">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Trusted-by */}
      {/* <TrustedBy /> */}
      <Dashboard />

      {/* Features */}
      <section id="features" className="py-14 sm:py-20 scroll-mt-24">
        <div className="container-marketing max-w-7xl mx-auto px-4">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <span className="inline-flex items-center rounded-full bg-brand/10 px-5 py-2 text-sm font-medium uppercase tracking-[0.18em] text-brand mb-4">
              What's included
            </span>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold mb-4 t-fg tracking-tight leading-[1.05]">
              Everything your studio needs to{" "}
              <span className="iridescent-text">stay fully booked.</span>
            </h2>
            <p className="t-fg-muted text-base sm:text-lg leading-relaxed">
              Designed specifically for hair salons, beauty studios and
              Australian service businesses.
            </p>
          </Reveal>

          <RevealStagger
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
            stagger={0.12}
          >
            {features.map((feature) => (
              <RevealItem key={feature.title}>
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 320, damping: 22 }}
                  className="feature-card h-full"
                >
                  <div
                    className="icon-wrapper mb-4 t-bg-accent-soft"
                    style={{ border: "1px solid var(--t-accent-border)" }}
                  >
                    <feature.icon className="w-5 h-5 t-text-accent" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2 t-fg">
                    {feature.title}
                  </h3>
                  <p className="t-fg-muted text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      <BookingFlow />
      {/* <Dashboard /> */}
      <Testimonials />

      {/* Pricing */}
      <section id="pricing" className="py-6 sm:py-8 relative scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <span className="inline-flex items-center rounded-full bg-brand/10 px-5 py-2 text-sm font-medium uppercase tracking-[0.18em] text-brand mb-4">
              <Crown className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
              Pricing
            </span>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight t-fg leading-[1.05]">
              Honest, <span className="iridescent-text">AUD pricing.</span>
            </h2>
            <p className="t-fg-muted mt-4 text-base sm:text-lg leading-relaxed">
              All plans include a 30-days free trial. No credit card required.
            </p>
          </Reveal>

          {apiPlans.length > 0 ? (
            <RevealStagger
              className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto"
              stagger={0.12}
            >
              {apiPlans.map((plan) => {
                const isPopular = plan.popular;
                return (
                  <RevealItem key={plan.id}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      transition={{
                        type: "spring",
                        stiffness: 320,
                        damping: 22,
                      }}
                      className="glass relative rounded-2xl p-7 flex flex-col h-full transition-shadow"
                      style={
                        isPopular
                          ? {
                              background: "var(--t-surface-strong)",
                              border: "2px solid var(--t-accent-border)",
                              boxShadow:
                                "0 24px 60px -20px var(--t-cta-glow), inset 0 0 0 1px var(--t-accent-soft)",
                              transform: "translateY(-6px)",
                            }
                          : {
                              background: "var(--t-surface)",
                              border: "1px solid var(--t-surface-border)",
                            }
                      }
                      data-testid={`landing-plan-${plan.id}`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full t-bg-cta"
                            style={{
                              fontFamily: '"Space Grotesk", monospace',
                            }}
                          >
                            Most Popular
                          </span>
                        </div>
                      )}

                      <div className="mb-6">
                        <h3 className="text-lg font-bold t-fg font-heading">
                          {plan.name}
                        </h3>
                        <div className="mt-3 flex items-baseline gap-1">
                          <span className="text-5xl font-extrabold t-fg font-heading tracking-tight">
                            ${plan.price}
                          </span>
                          <span className="t-fg-faint text-sm font-mono">
                            /{plan.interval}
                          </span>
                        </div>
                        {plan.subtitle && (
                          <p className="t-fg-muted text-sm mt-1.5">
                            {plan.subtitle}
                          </p>
                        )}
                      </div>

                      <ul className="space-y-3 mb-8 flex-1">
                        {(plan.features || []).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <Check className="w-4 h-4 mt-0.5 shrink-0 t-success" />
                            <span className="text-sm t-fg-muted">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <Link to="/auth/register">
                        <Button
                          className={`w-full h-11 font-semibold rounded-full transition-all ${
                            isPopular
                              ? "t-bg-cta noir-cta-glow"
                              : "t-bg-secondary"
                          }`}
                          data-testid={`landing-cta-${plan.id}`}
                        >
                          Start Free Trial
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </motion.div>
                  </RevealItem>
                );
              })}
            </RevealStagger>
          ) : (
            <div className="text-center py-8 t-fg-faint text-sm">
              Loading plans...
            </div>
          )}

          <p className="text-center mt-10 text-xs t-fg-faint font-mono uppercase tracking-widest">
            All prices AUD · GST inclusive · Cancel anytime
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 sm:py-20 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 iridescent-mesh-bg opacity-40"
        />
        <div className="container-marketing max-w-7xl mx-auto px-4 relative">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <span className="inline-flex items-center rounded-full bg-brand/10 px-5 py-2 text-sm font-medium uppercase tracking-[0.18em] text-brand mb-4">
              <ShieldCheck className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />
              Why SlotBook
            </span>
            <h2 className="font-heading text-3xl sm:text-5xl font-extrabold t-fg tracking-tight leading-[1.05]">
              Built for the way{" "}
              <span className="iridescent-text">real salons run.</span>
            </h2>
          </Reveal>
          <RevealStagger
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            stagger={0.08}
          >
            {benefits.map((benefit) => (
              <RevealItem key={benefit}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 320, damping: 22 }}
                  className="flex items-center gap-4 rounded-2xl p-5 h-full feature-card"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 t-bg-accent-soft"
                    style={{ border: "1px solid var(--t-accent-border)" }}
                  >
                    <CheckCircle className="w-5 h-5 t-text-accent" />
                  </div>
                  <span className="text-base font-medium t-fg">{benefit}</span>
                </motion.div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Outcomes Strip */}
      <section className="px-4 py-6 sm:py-8 relative">
        <div className="absolute inset-x-0 top-0 h-px t-divider-bg" />
        <div className="absolute inset-x-0 bottom-0 h-px t-divider-bg" />

        <div className="max-w-6xl mx-auto flex flex-col items-center">
          {/* First line: Headline style to stand out */}
          <Reveal delay={0.25}>
            <h2 className="text-center text-sm sm:text-base font-semibold text-foreground tracking-tight sm:tracking-normal">
              Typical results reported by businesses using online booking
              software
            </h2>
          </Reveal>

          {/* Stats Grid: Improved spacing and alignment */}
          <RevealStagger
            className="mt-6 w-full grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12"
            stagger={0.12}
          >
            {outcomes.map((o) => (
              <RevealItem key={o.label}>
                <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-5">
                  <div
                    className="flex size-14 items-center justify-center rounded-2xl flex-shrink-0 t-bg-accent-soft"
                    style={{ border: "1px solid var(--t-accent-border)" }}
                  >
                    <o.icon className="size-6 t-text-accent" />
                  </div>
                  <div className="flex flex-col">
                    <p className="font-heading text-5xl sm:text-6xl font-extrabold iridescent-text tracking-tight leading-none">
                      {o.kpi}
                    </p>
                    <p
                      className="mt-3 text-[12px] font-medium uppercase tracking-[0.2em] t-fg-muted"
                      style={{
                        fontFamily: '"Space Grotesk", ui-monospace, monospace',
                      }}
                    >
                      {o.label}
                    </p>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>

          {/* Bottom line: Subtle disclaimer style */}
          <Reveal delay={0.5}>
            <p className="mt-6 text-center text-[10px] sm:text-xs t-fg-faint font-mono uppercase tracking-[0.25em] max-w-2xl mx-auto leading-relaxed">
              Industry benchmarks from online booking and salon management
              software research. Individual results vary.
            </p>
          </Reveal>
        </div>
      </section>

      <Faq />
      <FinalCta onStart={handleGetStarted} onDemo={() => setDemoOpen(true)} />
      <LandingFooter />

      <VideoDemoModal open={videoOpen} onClose={() => setVideoOpen(false)} />

      <MobileStickyCTA
        onStart={handleGetStarted}
        onWatch={() => setVideoOpen(true)}
      />

      {/* Demo Modal */}
      {demoOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-md px-4">
          <div
            className="w-full max-w-md rounded-2xl p-8 relative shadow-2xl"
            style={{
              background: "var(--t-surface-strong)",
              border: "1px solid var(--t-surface-border)",
              color: "var(--t-fg)",
              boxShadow: "0 40px 100px -20px var(--t-cta-glow)",
            }}
          >
            <button
              onClick={() => {
                setDemoOpen(false);
                setDemoSuccess(false);
              }}
              className="absolute top-4 right-4 t-fg-muted t-hover-fg"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {!demoSuccess ? (
              <>
                <h2 className="text-2xl font-bold mb-2 text-center t-fg font-heading">
                  Book Your Free Demo
                </h2>
                <p className="text-sm t-fg-muted mb-6 text-center">
                  I'll personally walk you through SlotBook and answer your
                  questions.
                </p>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setLoading(true);
                    try {
                      await api.post("/demo-request", {
                        first_name: firstName,
                        salon_name: salonName,
                        email,
                        mobile,
                        current_system: currentSystem,
                      });
                      setDemoSuccess(true);
                      toast.success("Demo request submitted successfully!");
                      setFirstName("");
                      setSalonName("");
                      setEmail("");
                      setMobile("");
                      setCurrentSystem("Taking bookings via messages");
                    } catch (error) {
                      console.error("Demo request failed:", error);
                      toast.error("Something went wrong. Please try again.");
                    }
                    setLoading(false);
                  }}
                  className="space-y-3"
                >
                  {[
                    {
                      label: "First Name",
                      value: firstName,
                      setter: setFirstName,
                      type: "text",
                    },
                    {
                      label: "Salon Name",
                      value: salonName,
                      setter: setSalonName,
                      type: "text",
                    },
                    {
                      label: "Mobile Number",
                      value: mobile,
                      setter: setMobile,
                      type: "tel",
                    },
                    {
                      label: "Email",
                      value: email,
                      setter: setEmail,
                      type: "email",
                    },
                  ].map((f) => (
                    <div key={f.label}>
                      <label className="text-sm font-medium t-fg-muted">
                        {f.label}
                      </label>
                      <input
                        type={f.type}
                        required
                        value={f.value}
                        onChange={(e) => f.setter(e.target.value)}
                        className="w-full mt-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                        style={{
                          background: "var(--t-surface)",
                          border: "1px solid var(--t-surface-border)",
                          color: "var(--t-fg)",
                        }}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-sm font-medium t-fg-muted">
                      Currently using booking software?
                    </label>
                    <select
                      value={currentSystem}
                      onChange={(e) => setCurrentSystem(e.target.value)}
                      className="w-full mt-1 px-4 py-2 rounded-lg focus:outline-none focus:ring-2"
                      style={{
                        background: "var(--t-surface)",
                        border: "1px solid var(--t-surface-border)",
                        color: "var(--t-fg)",
                      }}
                    >
                      <option>Taking bookings via messages</option>
                      <option>Using booking software</option>
                      <option>Not sure</option>
                    </select>
                  </div>
                  <Button
                    type="submit"
                    className="w-full mt-4 t-bg-cta font-semibold"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Book My Free Demo"}
                  </Button>
                  <p className="text-xs t-fg-faint text-center mt-2">
                    No spam. No lock-in contracts.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 t-success" />
                <h2 className="text-2xl font-bold mb-2 t-fg font-heading">
                  You're All Set!
                </h2>
                <p className="t-fg-muted mb-6">
                  Thanks for booking your SlotBook demo. I'll contact you within
                  24 hours.
                </p>
                <Button
                  onClick={() => {
                    setDemoOpen(false);
                    setDemoSuccess(false);
                  }}
                  className="w-full t-bg-cta font-semibold"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
